import * as React from "react"
import { CalendarIcon } from "lucide-react"
import { format } from "date-fns"

import { cn } from "@/lib/utils"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover"
import { parseAsLocalDate } from "@/utils/dateHelpers"

interface DateRange {
  from: Date | undefined
  to: Date | undefined
}

interface DateRangePickerProps {
  date: DateRange
  onDateChange: (date: DateRange) => void
  className?: string
}

export function DateRangePicker({
  date,
  onDateChange,
  className
}: DateRangePickerProps) {
  const id = React.useId()
  const [startDate, setStartDate] = React.useState<string>(
    date.from ? format(date.from, "yyyy-MM-dd") : ""
  )
  const [endDate, setEndDate] = React.useState<string>(
    date.to ? format(date.to, "yyyy-MM-dd") : ""
  )

  const handleStartDateChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setStartDate(e.target.value)
    if (e.target.value) {
      onDateChange({
        from: parseAsLocalDate(e.target.value),
        to: date.to
      })
    }
  }

  const handleEndDateChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setEndDate(e.target.value)
    if (e.target.value) {
      onDateChange({
        from: date.from,
        to: parseAsLocalDate(e.target.value)
      })
    }
  }

  return (
    <div className={cn("grid gap-2", className)}>
      <Popover>
        <PopoverTrigger asChild>
          <Button
            id="date"
            variant={"outline"}
            className={cn(
              "justify-start text-left font-normal",
              !date.from && !date.to && "text-muted-foreground"
            )}
          >
            <CalendarIcon className="mr-2 h-4 w-4" />
            {date?.from ? (
              date.to ? (
                <>
                  {format(date.from, "LLL dd, y")} -{" "}
                  {format(date.to, "LLL dd, y")}
                </>
              ) : (
                format(date.from, "LLL dd, y")
              )
            ) : (
              <span>Pick a date range</span>
            )}
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-auto p-4" align="start">
          <div className="space-y-4">
            <div>
              <label htmlFor={`${id}-from`} className="text-sm font-medium">From</label>
              <Input
                id={`${id}-from`}
                type="date"
                value={startDate}
                onChange={handleStartDateChange}
                className="mt-1"
              />
            </div>
            <div>
              <label htmlFor={`${id}-to`} className="text-sm font-medium">To</label>
              <Input
                id={`${id}-to`}
                type="date"
                value={endDate}
                onChange={handleEndDateChange}
                className="mt-1"
              />
            </div>
            <Button
              variant="outline"
              className="w-full"
              onClick={() => {
                setStartDate("")
                setEndDate("")
                onDateChange({ from: undefined, to: undefined })
              }}
            >
              Clear
            </Button>
          </div>
        </PopoverContent>
      </Popover>
    </div>
  )
}
